import { useEffect } from 'react';

/**
 * Interact with Cookiebot event listener.
 * This should be inject in _app.tsx level so that we can capture the events from Cookiebot
 *    const listener = (): void => {
 *    if (window && window['Cookiebot']) {
 *        // Details of the given consent c.f Properties section in  https://www.cookiebot.com/en/developer/
 *      console.log({
 *          'window[Cookiebot]': window['Cookiebot']['consent'],
 *       });
 *     }
 *   };
 * @see _document.tsx for Cookiebot script
 * */
export const useCookieBotConsent = (listener: () => void): void => {
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Listeners for cookie bot event @see https://www.cookiebot.com/en/developer/
      window.addEventListener('CookiebotOnConsentReady', listener);
      window.addEventListener('CookiebotOnDialogDisplay', listener);
      window.addEventListener('CookiebotOnAccept', listener);
    }

    return () => {
      window.removeEventListener('CookiebotOnConsentReady', listener);
      window.removeEventListener('CookiebotOnDialogDisplay', listener);
      window.removeEventListener('CookiebotOnAccept', listener);
    };
  }, [listener]);
};

const RETRIES = 5;
const INTERVAL = 1000;

/**
 * Hook to add overflow-hidden class to the html element when Cookiebot is active.
 * This prevents scrolling when the cookie dialog is active.
 * The scroll lock is removed when consent is given or when the component unmounts.
 */
export const useLockScrollWhenCookieBotActive = (): void => {
  useEffect(() => {
    if (typeof window !== 'undefined' && window['Cookiebot']) {
      const { hasResponse } = window['Cookiebot'];

      // If user has already responded, no need to proceed
      if (hasResponse === true) return;

      // Function to apply the scroll lock
      const applyScrollLock = (): (() => void) => {
        // Add overflow hidden to prevent scrolling
        document.documentElement.classList.add('overflow-hidden');

        // Cleanup function for scroll lock
        const cleanup = () => {
          document.documentElement.classList.remove('overflow-hidden');
        };

        // Remove scroll lock when user responds (either accept or decline)
        window['CookiebotCallback_OnAccept'] = cleanup;
        window['CookiebotCallback_OnDecline'] = cleanup;

        return cleanup;
      };

      // Function to try finding the banner with retries
      const tryFindBanner = (
        retries = RETRIES,
        interval = INTERVAL,
        timeoutIds: Set<NodeJS.Timeout> = new Set()
      ): Promise<HTMLElement | null> => {
        return new Promise((resolve) => {
          const cookiebotBanner = document.getElementById('cookiebanner');

          if (cookiebotBanner) {
            resolve(cookiebotBanner);
            return;
          }

          if (retries <= 0) {
            resolve(null);
            return;
          }

          const timeoutId = setTimeout(() => {
            timeoutIds.delete(timeoutId);
            tryFindBanner(retries - 1, interval, timeoutIds).then(resolve);
          }, interval);
          timeoutIds.add(timeoutId);
        });
      };

      // Start the retry mechanism if user hasn't responded
      if (hasResponse !== undefined) {
        let cleanup: (() => void) | undefined;
        const timeoutIds = new Set<NodeJS.Timeout>();

        tryFindBanner(RETRIES, INTERVAL, timeoutIds).then((cookiebotBanner) => {
          if (cookiebotBanner) {
            cleanup = applyScrollLock();
          }
        });

        // Return cleanup function for component unmount
        return () => {
          // Cancel any pending timeouts
          timeoutIds.forEach(clearTimeout);
          timeoutIds.clear();

          if (cleanup) {
            cleanup();
          } else {
            // Fallback cleanup in case the banner was never found
            document.documentElement.classList.remove('overflow-hidden');
          }
        };
      }
    }

    return () => {
      document.documentElement.classList.remove('overflow-hidden');
    };
  }, []);
};
